import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import Logo from '../components/ui/Logo';
import toast from 'react-hot-toast';
import { SELLERCLUB_SUPPORT_PHONE, SELLERCLUB_SUPPORT_PHONE_DISPLAY } from '../constants/support';

export default function OnboardingStoreSelection() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [session, setSession] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [skippedCount, setSkippedCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      toast.error('Invalid session — please start Amazon sign-in again');
      navigate('/login');
      return;
    }

    (async () => {
      try {
        const res = await api.get('/onboarding/pending-stores', { params: { session: sessionId } });
        if (!res.data?.success) throw new Error(res.data?.error || 'Session expired');
        setSession(res.data.data);
        setSkippedCount(res.data.data?.skippedAlreadyConnected || 0);
        setSelected(new Set());
      } catch (error) {
        toast.error(error.response?.data?.error || error.message || 'Session expired');
        navigate('/login');
      } finally {
        setLoading(false);
      }
    })();
  }, [sessionId, navigate]);

  const toggle = (profileId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const id = String(profileId);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (selected.size === 0) {
      toast.error('Select at least one new store to connect');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/onboarding/confirm-stores', {
        sessionId,
        selectedProfileIds: [...selected],
      });
      if (!res.data?.success) throw new Error(res.data?.error || 'Failed to connect stores');

      if (res.data.token) {
        const storeCount = res.data.profileAccess?.length || selected.size;
        navigate(`/auth/amazon-callback?token=${encodeURIComponent(res.data.token)}&stores=${storeCount}`);
        return;
      }

      const names = (session?.profiles || [])
        .filter((p) => selected.has(String(p.profileId)))
        .map((p) => p.storeName)
        .join(',');
      navigate(
        `/onboarding/success?stores=${selected.size}&email=${encodeURIComponent(session?.email || '')}&names=${encodeURIComponent(names)}`
      );
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Failed to save store selection');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600" />
      </div>
    );
  }

  const profiles = session?.profiles || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-6 py-8 text-center text-white">
            <Logo className="justify-center mb-4" size="lg" />
            <h1 className="text-2xl font-bold">Connect new Amazon stores</h1>
            <p className="mt-2 text-orange-100 text-sm max-w-md mx-auto">
              Choose which stores to add to SellerClub. Stores already linked to your account are not shown here.
              After you submit, our team will review and approve access.
            </p>
          </div>

          <div className="p-6 space-y-4">
            {skippedCount > 0 && (
              <p className="text-sm text-slate-600 bg-orange-50 border border-orange-100 rounded-lg px-3 py-2">
                {skippedCount} store(s) already connected — use <strong>Login with Amazon</strong> to access them (no selection needed).
              </p>
            )}

            {profiles.length === 0 ? (
              <div className="text-center py-8 space-y-3">
                <p className="text-slate-800 font-medium">No new stores to add from this Amazon sign-in.</p>
                <p className="text-sm text-slate-600">
                  If you already onboarded, go back and use <strong>Login with Amazon</strong>.
                </p>
                <a
                  href={`tel:${SELLERCLUB_SUPPORT_PHONE}`}
                  className="inline-block text-sm font-semibold text-orange-700 hover:underline"
                >
                  Need help? Call {SELLERCLUB_SUPPORT_PHONE_DISPLAY}
                </a>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="mt-4 w-full py-3 rounded-xl bg-orange-600 text-white font-medium hover:bg-orange-700"
                >
                  Back to login
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{profiles.length} new store(s) available</span>
                  <button
                    type="button"
                    onClick={() => setSelected(new Set(profiles.map((p) => String(p.profileId))))}
                    className="text-orange-700 font-medium hover:underline"
                  >
                    Select all
                  </button>
                </div>

                <div className="space-y-2 max-h-[420px] overflow-y-auto">
                  {profiles.map((profile) => {
                    const id = String(profile.profileId);
                    const checked = selected.has(id);
                    return (
                      <label
                        key={id}
                        className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer transition-all ${
                          checked ? 'border-orange-400 bg-orange-50' : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(id)}
                          className="h-5 w-5 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-900">{profile.storeName || `Store ${id}`}</p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Profile {id} · {profile.countryCode || '—'} · {profile.currency || profile.currencyCode || '—'}
                          </p>
                        </div>
                      </label>
                    );
                  })}
                </div>

                <div className="flex gap-3 pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="flex-1 py-3 rounded-xl border border-slate-300 text-slate-700 font-medium hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={submitting || selected.size === 0}
                    onClick={handleConfirm}
                    className="flex-1 py-3 rounded-xl bg-orange-600 text-white font-semibold hover:bg-orange-700 disabled:opacity-50"
                  >
                    {submitting ? 'Submitting…' : `Connect ${selected.size || ''} store${selected.size === 1 ? '' : 's'}`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
